import logger from '@/lib/logger.js'
import { ForecastList, ForecastVarInventory, ForecastVarType, LayerInventory } from '@/lib/Forecast.js'
import UserPoint from '@/lib/UserPoint.js'
import ewpService from '@/lib/ewpService.js'
import events from '@/lib/events.js'
import UserData from './UserData'
import UserStorage from '@/lib/UserStorage.js'

const ServiceController = {
    init(store) {
        logger.log('ServiceController::init')

        if (store == null)
            throw new Error('ServiceController::create - store is null')

        if (events == null)
            throw new Error('ServiceController::create - events is null')

        this.store = store
        
        events.$on(events.EVT_USER_CHANGED, () => this.on_EVT_USER_CHANGED())
        events.$on(events.EVT_FORECAST_CHANGED, () => this.on_EVT_FORECAST_CHANGED())
        events.$on(events.EVT_FORECAST_INV_CHANGED, () => this.on_EVT_FORECAST_INV_CHANGED())
        events.$on(events.EVT_CONFIG_CHANGED, () => this.on_EVT_CONFIG_CHANGED())
    },
    on_EVT_CONFIG_CHANGED() {
        
    },
    on_EVT_USER_CHANGED() {
        logger.log('ServiceController::on_EVT_USER_CHANGED')

        this.store.commit('setUserPoints', null)
        this.store.commit('setForecast', null)
        this.store.commit('setForecastInventory', null)        

        if (this.store.state.user != null) {
            this.loadForecastInventory()
            this.loadUserPoints()
        }
        else
            logger.log('Not loading forecast inventory as no user specified')
    },
    on_EVT_FORECAST_INV_CHANGED() {
        logger.log('ServiceController::on_EVT_FORECAST_INV_CHANGED')

        if (this.store.state.forecast == null) {
            events.$emit(events.ACTION_OPEN_FORECAST)
        }
    },
    on_EVT_FORECAST_CHANGED() {
        logger.log('ServiceController::on_EVT_FORECAST_CHANGED')

        this.store.commit('setForecastVarInventory', null)

        this.loadForecastVarInventory()
        this.loadLayerInventory()
    },
    logout() {
        logger.log('ServiceController::logout()')
        let user = this.store.state.user

        ewpService.logout(user.usertoken)
            .then(() => {
                logger.log('Logout from server successful')
            })
            .catch((error) => {
                logger.log('Logout from server unsuccessful')
            })
            .then(() => {
                UserStorage.clear()
                this.store.commit('logout')                
            })
    },
    login(username, password, keepMeLoggedIn, onDone) {
        logger.log('ServiceController::login()', { username: username, password: '***'})
        let success = false

        ewpService.login(username, password)
            .then((resp) => {
                logger.log('Login data recieved', resp)                
                var userData = new UserData(resp.data)
                this.store.commit('setUser', userData)
                success = true
                
                if (keepMeLoggedIn) {
                    logger.log('Saving user token to UserStorage')
                    UserStorage.setUserToken(userData.usertoken)
                }
                else {
                    logger.log('Clearing user from UserStorage')
                    UserStorage.clear()
                }
            })
            .catch((error) => {
                logger.log('Error logging in', error)
                events.$emit(events.EVT_ERROR, 'Unable to login to your account. Please check your username/password and try again.')
            })
            .then(() => {
                if (onDone instanceof Function) {
                    onDone(success)
                }
            })
    },
    editPoint(id, lat, lon, name, onComplete) {
        logger.log('ServiceController::editPoint', { id: id, lat: lat, lon: lon, name: name, onComplete: onComplete })
        
        let user = this.store.state.user        
        
        if (user == null) {
            logger.log('Aborting ServiceController::editPoint - user is null')
            if (onComplete instanceof Function)
                onComplete(false);
            return
        }

        let success = false
        ewpService.editPoint(user.usertoken, id, lat, lon, name)
            .then((resp) => {
                logger.log('User point edited using service', resp)
                
                let userPoint = new UserPoint(resp.data.id, resp.data.cid, resp.data.lat, resp.data.lon, resp.data.name)

                this.store.commit('editPoint', userPoint)

                success = true
            })
            .catch((error) => {
                logger.log('Error editing point', error)
                events.$emit(events.EVT_ERROR, 'Error editing point')
            })
            .then(() => {
                if (onComplete instanceof Function)
                    onComplete(success);
            })
    },
    addPoint(lat, lon, name, onComplete) {
        logger.log('ServiceController::addPoint', { lat: lat, lon: lon, name: name, onComplete: onComplete })
        
        let user = this.store.state.user        
        let forecast = this.store.state.forecast
        
        if (user == null) {
            logger.log('Aborting ServiceController::addPoint - user is null')
            if (onComplete instanceof Function)
                onComplete(false);
            return
        }

        if (forecast == null) {
            logger.log('Abosrting ServiceController::addPoint - forecast is null')
            if (onComplete instanceof Function)
                onComplete(false);
            return
        }        

        let success = false
        ewpService.addPoint(user.usertoken, forecast.country.countryID, lat, lon, name)
            .then((resp) => {
                logger.log('User point added using service', resp)
                
                let userPoint = new UserPoint(resp.data.id, resp.data.cid, resp.data.lat, resp.data.lon, resp.data.name)

                this.store.commit('addPoint', userPoint)

                success = true
            })
            .catch((error) => {
                logger.log('Error adding point', error)
                events.$emit(events.EVT_ERROR, 'Error adding point')
            })
            .then(() => {
                if (onComplete instanceof Function)
                    onComplete(success);
            })
    },
    loadLayerInventory() {
        logger.log('ServiceController::loadLayerInventory')

        let user = this.store.state.user
        let forecast = this.store.state.forecast

        if (user == null || forecast == null) {
            logger.log('Aborting ServiceController::loadLayerInventory()')
            return
        }

        ewpService.getLayerInventory(user.usertoken, forecast.country.countryID)
            .then((resp) => {
                logger.log('Layer inventory data received', resp)

                let inventory = new LayerInventory(resp.data)
                logger.log('Loaded layer inventory', inventory)

                this.store.commit('setLayerInventory', inventory)
            })
            .catch((error) => {
                logger.log('Error loading layer inventory', error)
                events.$emit(events.EVT_ERROR, 'Error loading layers inventory')
            })
    },
    loadForecastInventory() {
        logger.log('ServiceController::loadForecastInventory')

        let user = this.store.state.user

        if (user == null) {
            logger.log('Aborting ServiceController::loadForecastInventory()')
            return
        }

        ewpService.getForecastInventory(user.usertoken)
            .then((resp) => {
                logger.log('Forecast Inventory data recieved', resp)

                let inventory = new ForecastList(resp.data.items)                

                this.store.commit('setForecastInventory', inventory)
            })
            .catch((error) => {
                logger.log('Error loading forecast inventory', error)                
                events.$emit(events.EVT_ERROR, 'Error loading forecast inventory')
            })            
    },
    getUserInfo(usertoken, onSuccess, onError) {
        logger.log('ServiceController::getUserInfo()')

        ewpService.getUserInfo(usertoken)
            .then((resp) => {
                logger.log('UserInfo data recieved', resp)
                let data = resp.data
                if (onSuccess instanceof Function) {
                    let userData = new UserData(data)
                    logger.log('Created userData', userData)
                    onSuccess(userData)
                }
            })
            .catch((error) => {
                logger.log('Error getting user info', error)
                if (onError instanceof Function) {
                    onError()
                }
            })
    },
    loadForecastVarInventory() {
        logger.log('ServiceController::loadForecastVarInventory')

        let user = this.store.state.user
        let forecast = this.store.state.forecast
        
        if (user == null || forecast == null) {
            logger.log('Aborting ServiceController::loadForecastVarInventory()')
            return
        }
        
        ewpService.getForecastVarInventory(user.usertoken, forecast.country.countryID, forecast.forecastID.toString())
            .then((resp) => {
                logger.log('Forecast Var Inventory data received', resp)

                let inventory = new ForecastVarInventory(resp.data)

                logger.log('ForecastVarInventory object created', inventory)
                
                this.store.commit('setForecastVarInventory', inventory)
            })
            .catch((error) => {
                logger.log('Error loading forecast var inventory', error)
                events.$emit(events.EVT_ERROR, 'Error loading forecast variable inventory')
            })
    },
    deleteUserPoint(pointID) {
        logger.log('ServiceController::deleteUserPoint()')

        let user = this.store.state.user

        if (user == null) {
            logger.log('Aborting ServiceController::loadUserPoints() - user not loaded')
            return            
        }

        ewpService.deletePoint(user.usertoken, pointID)
            .then(() => {
                logger.log('User point deleted')

                this.store.commit('deleteUserPoint', pointID)
            })
            .catch((error) => {
                logger.log('Error deleteing user point', error)

                events.$emit(events.EVT_ERROR, 'There was an error deleteing ths specified point')
            })
    },
    loadUserPoints() {
        logger.log('ServiceController::loadUserPoints()')

        let user = this.store.state.user

        if (user == null) {
            logger.log('Aborting ServiceController::loadUserPoints() - user not loaded')
            return            
        }

        ewpService.getPoints(user.usertoken)
            .then((resp) => {
                logger.log('User points data recieved', resp)

                let points = resp.data.points.map((point) => new UserPoint(point))                

                this.store.commit('setUserPoints', points)
            })
            .catch((error) => {
                logger.log('Error loading user points', error)
            })
    },
    readVarValues(variable, forecast, lat, lon, onSuccess, onError) {
        let user = this.store.state.user

        let forecastIDValue = ''
        if (variable.varType == ForecastVarType.VT_HISTORICAL)
            forecastIDValue = 'historical'
        else    
            forecastIDValue = forecast.forecastID.toString(true)

        ewpService.readVarValues(user.usertoken, forecast.country.countryID, forecastIDValue, variable.varID, lat, lon)
                .then(resp => {
                    logger.log('Recieved values', resp)

                    if (onSuccess instanceof Function)
                        onSuccess(resp.data.values)
                })
                .catch(error => {
                    logger.log('Error reading value', error)

                    events.$emit(events.EVT_ERROR, 'Error loading point information')                    

                    if (onError instanceof Function)
                        onError()
                })
    },
    defaultReportData(onSuccess, onError) {
        logger.log('ServiceController::defaultReportData()')

        let user = this.store.state.user
        let forecast = this.store.state.forecast
        
        if (user == null || forecast == null) {
            logger.log('Aborting ServiceController::defaultReportData()')
            return
        }

        let usertoken = user.usertoken
        let countryID = forecast.country.countryID
        let forecastID = forecast.forecastID.toString()

        logger.log('Making request with params', { usertoken: usertoken, countryID: countryID, forecastID: forecastID })

        ewpService.defaultReportData(usertoken, countryID, forecastID)
            .then((resp) => {
                logger.log('DefaultReportData received', resp)
                if (onSuccess instanceof Function)
                    onSuccess(resp.data)
            })
            .catch((error) => {
                logger.log('Error getting DefaultReportData', error)
                if (onError instanceof Function)
                    onError(error.message)
            })
    },
    loadConfig() {
        logger.log('ServiceController::loadConfig')

        let user = this.store.state.user

        if (user == null) {
            logger.log('Aborting ServiceController::loadConfig, user is null')
            return
        }

        let usertoken = user.usertoken

        ewpService.getConfig(usertoken)
            .then((resp) => {
                logger.log('Config data received', resp)
                this.store.state.commit('setConfig', resp)
            })
            .catch((error) => {
                logger.log('Error getting config', error)
            })
    },
    updateConfigProperty(key, value) {
        logger.log('ServiceController::updateConfigProperty')
        
        let user = this.store.state.user

        if (user == null) {
            logger.log('Aborting ServiceController::updateConfigProperty, user is null')
            return
        }

        let usertoken = user.usertoken

        ewpService.updateConfigProperty(usertoken, key, value)
            .then((resp) => {
                logger.log('Config updated, and new data recieved', resp)
                this.store.state.commit('setConfig', resp.data)
            })
            .catch((error) => {
                logger.log('Error updating config property'. error)
            })
    }
}

export { ServiceController }